import * as React from "react";

import {
  Route,
  RouteComponentProps,
  Switch
} from "react-router-dom";

import { cloneDeep, flatten, isNil, uniqBy } from "lodash";
import { toast, ToastOptions } from "react-toastify";

import { AccessReportsService } from "@app/access-reports";
import {
  AccessReportsAdminAddProduct,
  AccessReportsAdminDashboard,
  AccessReportsAdminVersionTypeEnum
} from "@app/access-reports/admin";
import {
  AccessReportsCoverPageElementEnum,
  AccessReportsPrintOptions,
  AccessReportsProductDefaultElementsEnum,
  AccessReportsWizardElement,
  AccessReportsWizardModel,
  AccessReportsWizardOption,
  AccessReportsWizardPage,
  AccessReportsWizardProduct,
  AccessReportsWizardProviderSet,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
  AccessReportsWizardVersionModel,
} from "@app/access-reports/shared";
import { assignTemplateMultiElementOptions, getElementByIdFromTemplate, getPageFromTemplate } from "@app/access-reports/shared/utility";
import { TemplateUpdate } from "@app/access-reports/admin/template-update";
import { UserInfo, UserService } from "@app/user";
import { UserModelFromApi } from "@app/admin/user-administration/userModel";
import { ProviderSetService } from "@app/provider-sets";
import { ProviderSetVersionModel } from "@app/provider-sets/providerSet";
import { focusFirstInteractiveElement } from "@app/shared/utility";

type AccessReportsAdminProps = RouteComponentProps<unknown> & {
  user: UserInfo;
}

interface AccessReportsAdminState {
  accessReportsWizard: AccessReportsWizardModel;
  checkedOutUsername: string;
  disableUserActions: boolean;
  isCheckedOutByCurrentUser: boolean;
  loading: boolean;
  selectedVersion: AccessReportsWizardVersionModel;
  selectedVersionType: AccessReportsAdminVersionTypeEnum;
}

class AccessReportsAdmin extends React.Component<AccessReportsAdminProps, AccessReportsAdminState> {
  private readonly accessReportsService = new AccessReportsService();
  private readonly providerSetService = new ProviderSetService();
  private readonly userService = new UserService();

  constructor(props: AccessReportsAdminProps) {
    super(props);

    this.state = {
      accessReportsWizard: null,
      checkedOutUsername: null,
      disableUserActions: true,
      isCheckedOutByCurrentUser: false,
      loading: true,
      selectedVersion: null,
      selectedVersionType: null,
    };
  }

  public async componentDidMount() {
    await this.setAccessReportsWizard();
  }

  public componentDidUpdate(prevProps: AccessReportsAdminProps, prevState: AccessReportsAdminState) {
    if (prevState.loading && !this.state.loading) {
      focusFirstInteractiveElement();
    }
  }

  public render() {
    return (
      <Switch>
        <Route
          exact={true}
          path={`${this.props.match.url}/dashboard`}
          render={(props) =>
            <AccessReportsAdminDashboard
              {...props}
              accessReportsWizard={this.state.accessReportsWizard}
              checkedOutUsername={this.state.checkedOutUsername}
              disableUserActions={this.state.disableUserActions}
              onCheckInClick={this.onCheckInClick}
              onCheckOutClick={this.onCheckOutClick}
              isCheckedOutByCurrentUser={this.state.isCheckedOutByCurrentUser}
              loading={this.state.loading}
              onActivateClick={this.onActivateClick}
              onCoverPageDefaultValueChange={this.onCoverPageDefaultValueChange}
              onCoverPageToggle={this.onCoverPageToggle}
              onCreateNewVersionClick={this.onCreateNewVersionClick}
              onDeleteVersionClick={this.onDeleteVersionClick}
              onPrintOptionsChange={this.onPrintOptionsChange}
              onRemoveProductsClick={this.onRemoveProductsClick}
              onResetPrintSettingsClick={this.onResetPrintSettingsClick}
              onSaveClick={this.onSaveClick}
              onSaveLogosClick={this.onSaveLogosClick}
              onSaveProductsClick={this.onSaveProductsClick}
              onSaveProductTypesClick={this.onSaveProductTypesClick}
              onSelectVersionTypeClick={this.onSelectVersionTypeClick}
              selectedVersion={this.state.selectedVersion}
              selectedVersionType={this.state.selectedVersionType}
            />
          }
        />
        <Route
          path={`${this.props.match.url}/product/:productId`}
          render={(props) =>
            <AccessReportsAdminAddProduct
              {...props}
              productId={parseInt((props.match.params).productId, 10)}
              disableUserActions={this.state.disableUserActions}
              onSaveProductClick={this.onSaveProductClick}
              template={this.state.selectedVersion?.template}
            />
          }
        />
        <Route
          path={`${this.props.match.url}/template`}
          component={TemplateUpdate}
        />
      </Switch>
    );
  }

  public onActivateClick = async (): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      // first, archive the current version
      const currentVersion = this.state.accessReportsWizard.accessReportWizardVersions.find(x => !x.checkedOutDate && !!x.dateActivated);

      if (!!currentVersion) {
        await this.createProviderSetCopies(currentVersion.template);

        const archived: boolean = await this.archiveVersion(
          currentVersion.accessReportWizardId,
          currentVersion.accessReportWizardVersionId,
        );

        if (archived) {
          // after the current version has been successfully archived,
          // activate the in-progress version
          const activated: boolean = await this.activateVersion(
            this.state.selectedVersion?.accessReportWizardId,
            this.state.selectedVersion?.accessReportWizardVersionId,
          );

          if (activated) {
            // once this new version has been activated, check it in
            const checkedIn: boolean = await this.checkInVersion(
              this.state.selectedVersion?.accessReportWizardId,
              this.state.selectedVersion?.accessReportWizardVersionId,
            );

            if (checkedIn) {
              const toastOptions: ToastOptions = {
                closeOnClick: true,
                containerId: "z-index-9999",
              };

              toast.info("Wizard Activated", toastOptions);

              // and finally, once it's been checked in, retrieve the template again
              await this.setAccessReportsWizard();
            }
          }
        }
      }
    });
  }

  public onCheckInClick = async (): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      // check in the currently-selected in-progress version
      const checkedIn: boolean = await this.checkInVersion(
        this.state.selectedVersion?.accessReportWizardId,
        this.state.selectedVersion?.accessReportWizardVersionId,
      );

      if (checkedIn) {
        // once it's been checked in, retrieve the template again
        await this.setAccessReportsWizard();
      }
    });
  }

  public onCheckOutClick = async (): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      // if the access reports wizard returns a version of the template that has not been activated,
      // then check that version out, as it is currently in-progress
      const inProgressVersion: AccessReportsWizardVersionModel = this.state.accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);

      if (!!inProgressVersion) {
        // check out the template
        const checkedOut: boolean = await this.checkOutVersion(
          inProgressVersion.accessReportWizardId,
          inProgressVersion.accessReportWizardVersionId,
        );

        if (checkedOut) {
          // once it's been checked out, retrieve the template again
          await this.setAccessReportsWizard();
        }
      } else {
        console.error("Unable to check-out a version of the access report template");
      }
    });
  }

  public onCoverPageDefaultValueChange = (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>): void => {
    if (!!this.state.selectedVersion) {
      const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
      const coverPage: AccessReportsWizardPage = selectedVersion.template?.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

      for (const formGroup of coverPage?.formGroups) {
        const element: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === elementId);

        if (!!element) {
          element.defaultValue = event.target.value;

          this.setState({
            selectedVersion,
          });
        }
      }
    }
  }

  public onCoverPageToggle = (elementId: string, key: keyof AccessReportsWizardElement): void => {
    if (!!this.state.selectedVersion) {
      const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
      const coverPage: AccessReportsWizardPage = selectedVersion.template?.pages.find((x) => x.title === AccessReportsWizardStepEnum.CoverPage);

      for (const formGroup of coverPage?.formGroups) {
        const element: AccessReportsWizardElement = formGroup.elements.find((x) => x.elementId === elementId);

        if (!!element) {
          // these elements are checkboxes, so boolean makes sense
          ((element)[key] as boolean) = !((element)[key]);

          this.setState({
            selectedVersion,
          });
        }
      }
    }
  }

  public onCreateNewVersionClick = async (): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      // create a new version of the access reports wizard
      // based off of the currently-activated template
      const currentTemplate: AccessReportsWizardTemplate = this.state.accessReportsWizard.accessReportWizardVersions.find(x => !!x.dateActivated)?.template;

      if (!!currentTemplate) {
        const newVersion: AccessReportsWizardVersionModel = await this.createNewVersion(
          this.state.accessReportsWizard.accessReportWizardId,
          currentTemplate,
        );

        if (!!newVersion) {
          this.setState({
            selectedVersion: newVersion,
          });

          await this.setAccessReportsWizard();
        }
      }
    });
  }

  public onDeleteVersionClick = async (): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      // deleted the currently-selected in progress version
      const deleted: boolean = await this.deleteVersion(
        this.state.selectedVersion?.accessReportWizardId,
        this.state.selectedVersion?.accessReportWizardVersionId,
      );

      if (deleted) {
        // once it's been deleted, retrieve the template again
        await this.setAccessReportsWizard();
      }
    });
  }

  public onPrintOptionsChange = (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean): void => {
    if (!!this.state.selectedVersion) {
      const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
      const printOptionsElement: AccessReportsWizardElement = selectedVersion.template?.pages.find(
        (x) => x.title === AccessReportsWizardStepEnum.PrintOptions)?.formGroups.find(
          (x) => x.id === formGroupId)?.elements.find(
            (x) => x.elementId === key);

      if (!!printOptionsElement) {
        printOptionsElement.defaultValue = defaultValue;

        this.setState({
          selectedVersion,
        });
      }
    }
  }

  public onRemoveProductsClick = async (products: AccessReportsWizardProduct[]): Promise<void> => {
    this.setState({
      loading: true,
    }, async () => {
      const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);

      for (const product of products) {
        // remove connections to elements with product-specific defaults
        for (const elementId of Object.values(AccessReportsProductDefaultElementsEnum)) {
          if (elementId !== AccessReportsProductDefaultElementsEnum.ProductType) {
            let page: AccessReportsWizardPage;

            switch (elementId) {
              case AccessReportsProductDefaultElementsEnum.AccessStandard:
              case AccessReportsProductDefaultElementsEnum.CalculationMethod:
              case AccessReportsProductDefaultElementsEnum.DistanceMethod:
                page = getPageFromTemplate(selectedVersion.template, AccessReportsWizardStepEnum.AccessAnalysis);

                if (!!page) {
                  const element: AccessReportsWizardElement = getElementByIdFromTemplate(selectedVersion.template, page.title, elementId);

                  if (!!element?.options) {
                    for (const option of element.options) {
                      option.defaultProducts = option.defaultProducts.filter(x => x !== product.id);
                      option.hiddenProducts = option.hiddenProducts.filter(x => x !== product.id);
                    }
                  }
                }

                break;
              case AccessReportsProductDefaultElementsEnum.Product:
              case AccessReportsProductDefaultElementsEnum.ReportTitleOne:
              case AccessReportsProductDefaultElementsEnum.ReportTitleTwo:
              case AccessReportsProductDefaultElementsEnum.ReportTitleThree:
                page = getPageFromTemplate(selectedVersion.template, AccessReportsWizardStepEnum.CoverPage);

                if (!!page) {
                  const element: AccessReportsWizardElement = getElementByIdFromTemplate(selectedVersion.template, page.title, elementId);

                  if (!!element) {
                    element.options = element.options.filter(x => x.id !== product.id);
                  }
                }

                break;
              default:
                console.error(`Invalid product default element with connections: ${elementId}`);
                break;
            }
          }
        }

        const pageOptionsPage: AccessReportsWizardPage = getPageFromTemplate(selectedVersion.template, AccessReportsWizardStepEnum.PageOptions);

        for (const pageFormGroup of pageOptionsPage.formGroups) {
          if (!!pageFormGroup.groupPropertyId) {
            // remove by product defaults and column defaults from pages
            if (!!pageFormGroup.byProductDefaults) {
              pageFormGroup.byProductDefaults = pageFormGroup.byProductDefaults.filter(x => x.productId !== product.id);
            }

            if (!!pageFormGroup.columnDefaults) {
              pageFormGroup.columnDefaults = pageFormGroup.columnDefaults.filter(x => x.productId !== product.id);
            }

            pageFormGroup.disallowedMultipleProducts = pageFormGroup.disallowedMultipleProducts.filter(x => x !== product.id);
            pageFormGroup.hiddenProducts = pageFormGroup.hiddenProducts.filter(x => x !== product.id);
          } else {
            // remove page options
            for (const element of pageFormGroup.elements) {
              element.options = element.options.filter(x => x.id !== product.id);
            }
          }
        }
      }

      await this.onSaveVersionEvent(selectedVersion);
    });
  }

  public onResetPrintSettingsClick = (): void => {
    const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
    const printOptionsPage: AccessReportsWizardPage = selectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.PrintOptions);
    const printOptionsElements: AccessReportsWizardElement[] = flatten(printOptionsPage?.formGroups.map(x => x.elements));

    for (const element of printOptionsElements) {
      if (element.originalDefaultValue !== null || element.originalDefaultValue !== undefined) {
        element.defaultValue = element.originalDefaultValue;
      } else {
        console.error(`The template does not contain an original default value for ${element.elementId}`);
      }
    }

    this.setState({
      selectedVersion,
    });
  }

  public onSaveProductClick = async (template: AccessReportsWizardTemplate): Promise<void> => {
    if (this.state.isCheckedOutByCurrentUser) {
      this.setState({
        loading: true,
      }, async () => {
        const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
        selectedVersion.template = template;

        await this.onSaveVersionEvent(selectedVersion);
      });
    }
  }

  public onSaveClick = async (): Promise<void> => {
    if (this.state.isCheckedOutByCurrentUser) {
      this.setState({
        loading: true,
      }, async () => {
        const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);

        await this.onSaveVersionEvent(selectedVersion);
      });
    }
  }

  public onSaveLogosClick = async (logos: AccessReportsWizardOption[], reloadPage: boolean): Promise<void> => {
    if (this.state.isCheckedOutByCurrentUser) {
      const handleSaveLogos = async () => {
        const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
        const coverPage: AccessReportsWizardPage = selectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
        const logoElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === "logo");

        if (!!logoElement) {
          // find all removed logo ids
          const previousLogoIds: number[] = logoElement.options.map(x => x.id);
          const newLogoIds: number[] = logos.map(x => x.id);
          const removedLogoIds: number[] = previousLogoIds.filter(x => !newLogoIds.includes(x));

          // archive any removed logos
          for (const removedLogoId of removedLogoIds) {
            await this.archiveLogo(removedLogoId);
          }

          logoElement.options = logos;

          await this.onSaveVersionEvent(selectedVersion);
        }
      }

      if (reloadPage) {
        this.setState({
          loading: true,
        }, async () => {
          handleSaveLogos();
        });
      } else {
        handleSaveLogos();
      }
    }
  }

  public onSaveProductsClick = async (products: AccessReportsWizardOption[]): Promise<void> => {
    if (this.state.isCheckedOutByCurrentUser) {
      this.setState({
        loading: true,
      }, async () => {
        const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
        const coverPage: AccessReportsWizardPage = selectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
        const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);

        if (!!productElement) {
          productElement.options = products;

          await this.onSaveVersionEvent(selectedVersion);
        }
      });
    }
  }

  public onSaveProductTypesClick = async (productTypes: AccessReportsWizardOption[]): Promise<void> => {
    if (this.state.isCheckedOutByCurrentUser) {
      this.setState({
        loading: true,
      }, async () => {
        const selectedVersion: AccessReportsWizardVersionModel = cloneDeep(this.state.selectedVersion);
        const coverPage: AccessReportsWizardPage = selectedVersion.template?.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
        const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
        const productTypeElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.ProductType);

        if (!!productTypeElement) {
          productTypeElement.options = productTypes;

          await this.onSaveVersionEvent(selectedVersion);
        }
      });
    }
  }

  public onSelectVersionTypeClick = (selectedVersionType: AccessReportsAdminVersionTypeEnum): void => {
    let selectedVersion: AccessReportsWizardVersionModel = null;

    switch (selectedVersionType) {
      case AccessReportsAdminVersionTypeEnum.CurrentVersion:
        // displays the currently-activated version of the access reports wizard
        const currentVersion: AccessReportsWizardVersionModel = this.state.accessReportsWizard.accessReportWizardVersions.find(x => !x.checkedOutDate && !!x.dateActivated);

        if (!!currentVersion) {
          selectedVersion = currentVersion;
        } else {
          console.error(`Unable to find the ${selectedVersionType} in access reports wizard ${this.state.accessReportsWizard.accessReportWizardId}`);
        }

        break;
      case AccessReportsAdminVersionTypeEnum.InProgressVersion:
        // attempts to retrieve an in-progress version of the access reports wizard;
        // if one does not exist, the user will be given the option to create one
        const inProgressVersion: AccessReportsWizardVersionModel = this.state.accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);

        if (!!inProgressVersion) {
          selectedVersion = inProgressVersion;
        }

        break;
      default:
        console.error(`Unable to find the version type ${selectedVersionType}`);
        break;
    }

    const disableUserActions: boolean = this.disableUserActions(this.state.isCheckedOutByCurrentUser, selectedVersionType);

    this.setState({
      disableUserActions,
      selectedVersion,
      selectedVersionType,
    });
  }

  private createNewAccessReportsWizardModel = async (): Promise<AccessReportsWizardModel> => {
    let accessReportsWizard: AccessReportsWizardModel = null;

    // if the access reports wizard model does not exist, first, obtain the template via the "old" way;
    // this api call should be updated to include the retrieval of the "skeleton" template if an "old" way template does not exist
    const accessReportsWizardTemplate: AccessReportsWizardTemplate = await this.accessReportsService.getAccessReportsTemplate();

    // next, create the new access reports wizard model for this client
    if (!!accessReportsWizardTemplate) {
      accessReportsWizard = await this.createWizard(accessReportsWizardTemplate);

      // now activate the newly-created version
      if (!!accessReportsWizard) {
        const unactivatedVersion = accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated && !x.dateArchived && !x.datePurged);

        if (!!unactivatedVersion) {
          // in order to activate this version, it needs to be checked out
          const checkedOut: boolean = await this.checkOutVersion(
            unactivatedVersion.accessReportWizardId,
            unactivatedVersion.accessReportWizardVersionId,
          );

          if (checkedOut) {
            const activated: boolean = await this.activateVersion(
              unactivatedVersion.accessReportWizardId,
              unactivatedVersion.accessReportWizardVersionId
            );

            if (activated) {
              // check it right back in
              const checkedIn: boolean = await this.checkInVersion(
                unactivatedVersion.accessReportWizardId,
                unactivatedVersion.accessReportWizardVersionId,
              );

              if (checkedIn) {
                // and finally, return the new model
                accessReportsWizard = await this.accessReportsService.getAccessReportsWizard();
              }
            }
          }
        }
      }
    }

    return accessReportsWizard;
  }

  private activateVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const activated: boolean = await this.accessReportsService.activateAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId
    );

    if (activated) {
      return true;
    } else {
      console.error(`Unable to activate the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return false;
    }
  }

  private archiveLogo = async (
    logoId: number,
  ): Promise<void> => {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this.accessReportsService.archiveLogo(
          logoId,
        );

        resolve();
      } catch {
        console.error(`Unable to archive logo ${logoId}`);
        reject();
      }
    });
  }

  private archiveVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const archived: boolean = await this.accessReportsService.archiveAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId
    );

    if (archived) {
      return true;
    } else {
      console.error(`Unable to archive the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return false;
    }
  }

  private checkInVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const checkedIn: boolean = await this.accessReportsService.checkInAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (checkedIn) {
      return true;
    } else {
      console.error(`Unable to check-in the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return false;
    }
  }

  private checkOutVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const checkedOut: boolean = await this.accessReportsService.checkOutAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (checkedOut) {
      return true;
    } else {
      console.error(`Unable to check-out the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return false;
    }
  }

  private createNewVersion = async (
    accessReportWizardId: number,
    template: AccessReportsWizardTemplate,
  ): Promise<AccessReportsWizardVersionModel> => {
    const newVersion: AccessReportsWizardVersionModel = await this.accessReportsService.createNewAccessReportsWizardVersion(
      accessReportWizardId,
      template,
    );

    if (!!newVersion) {
      return newVersion;
    } else {
      console.error(`Unable to create a new wizard version for wizard id ${accessReportWizardId}`);
      return null;
    }
  }

  private createProviderSetCopies = async (
    template: AccessReportsWizardTemplate
  ) => {
    const extractProviderSets = (template: AccessReportsWizardTemplate): AccessReportsWizardProviderSet[] => {
      const coverPage: AccessReportsWizardPage = template.pages.find(x => x.title === AccessReportsWizardStepEnum.CoverPage);
      const coverPageElements: AccessReportsWizardElement[] = flatten(coverPage?.formGroups.map(x => x.elements));
      const productElement: AccessReportsWizardElement = coverPageElements.find(x => x.elementId === AccessReportsCoverPageElementEnum.Product);
      const allProducts: AccessReportsWizardProduct[] = productElement?.options as AccessReportsWizardProduct[];

      return flatten(allProducts.map(x => x.accessCriterion.map(y => y.providerSet)));
    }

    const oldProviderSets: AccessReportsWizardProviderSet[] = extractProviderSets(template);
    const newProviderSets: AccessReportsWizardProviderSet[] = extractProviderSets(this.state.selectedVersion.template);
    const providerSetsToCopy: AccessReportsWizardProviderSet[] = uniqBy(newProviderSets.filter(x => !oldProviderSets.map(y => y.providerSetId).includes(x.providerSetId)), "providerSetId");
    const nonVersionedProviderSetIds: number[] = providerSetsToCopy.filter(x => !x.providerSetVersionId).map(x => x.providerSetId);

    if (nonVersionedProviderSetIds.length) {
      const providerSetVersionModels: ProviderSetVersionModel[] = await this.providerSetService.getLatestProviderSetVersion(nonVersionedProviderSetIds, this.props.user.clientId);

      if (!!providerSetVersionModels.length) {
        for (const providerSetVersionModel of providerSetVersionModels) {
          const providerSetToUpdate: AccessReportsWizardProviderSet = providerSetsToCopy.find(x => x.providerSetId === providerSetVersionModel.providerSetId);

          if (!!providerSetToUpdate) {
            providerSetToUpdate.providerSetVersionId = providerSetVersionModel.providerSetVersionId;
          }
        }
      }
    }

    for (const providerSet of providerSetsToCopy) {
      const providerSetCopySuccess = await this.accessReportsService.createProviderSetCopy(providerSet);

      if (!providerSetCopySuccess) {
        console.error(`Unable to create provider set copy for ${providerSet.providerSetId}`)
      }
    }
  }

  private createWizard = async (
    template: AccessReportsWizardTemplate
  ): Promise<AccessReportsWizardModel> => {
    const accessReportsWizard: AccessReportsWizardModel = await this.accessReportsService.createAccessReportsWizard(template);

    if (!!accessReportsWizard) {
      return accessReportsWizard;
    } else {
      console.error("Unable to create an access reports wizard");
      return null;
    }
  }

  private deleteVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
  ): Promise<boolean> => {
    const deleted: boolean = await this.accessReportsService.deleteAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
    );

    if (deleted) {
      return true;
    } else {
      console.error(`Unable to delete the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return false;
    }
  }

  private disableUserActions = (
    isCheckedOutByCurrentUser: boolean,
    selectedVersionType: AccessReportsAdminVersionTypeEnum,
  ): boolean => {
    if (selectedVersionType === AccessReportsAdminVersionTypeEnum.CurrentVersion || !isCheckedOutByCurrentUser) {
      return true;
    } else {
      return false;
    }
  }

  private getWizard = async (): Promise<AccessReportsWizardModel> => {
    let accessReportsWizard: AccessReportsWizardModel = null;

    try {
      accessReportsWizard = await this.accessReportsService.getAccessReportsWizard();
    }
    // the catch variable is _only_ allowed to be any or unknown in TS as of version 4.4
    catch (e: unknown) {
      if (!accessReportsWizard && (e as { status: number }).status === 404) {
        accessReportsWizard = await this.createNewAccessReportsWizardModel();
      }
    }

    return accessReportsWizard;
  }

  private onSaveVersionEvent = async (selectedVersion: AccessReportsWizardVersionModel): Promise<void> => {
    const savedAccessReportsWizardModel: AccessReportsWizardVersionModel = await this.saveVersion(
      selectedVersion.accessReportWizardId,
      selectedVersion.accessReportWizardVersionId,
      selectedVersion.template,
    );

    if (!!savedAccessReportsWizardModel) {
      await this.setAccessReportsWizard();
    }
  }

  private saveVersion = async (
    accessReportWizardId: number,
    accessReportWizardVersionId: number,
    template: AccessReportsWizardTemplate,
  ): Promise<AccessReportsWizardVersionModel> => {
    const savedAccessReportsWizardModel: AccessReportsWizardVersionModel = await this.accessReportsService.saveAccessReportsWizardVersion(
      accessReportWizardId,
      accessReportWizardVersionId,
      template,
    );

    if (!!savedAccessReportsWizardModel) {
      return savedAccessReportsWizardModel;
    } else {
      console.error(`Unable to save the wizard version ${accessReportWizardVersionId} for wizard id ${accessReportWizardId}`);
      return null;
    }
  }

  private setAccessReportsWizard = async (): Promise<void> => {
    const accessReportsWizard: AccessReportsWizardModel = await this.getWizard();

    for (const version of accessReportsWizard.accessReportWizardVersions) {
      const assignedTemplate: AccessReportsWizardTemplate = assignTemplateMultiElementOptions(version.template);
      version.template = assignedTemplate;
    }

    let checkedOutUsername = "";
    let isCheckedOutByCurrentUser = false;
    let selectedVersion: AccessReportsWizardVersionModel = this.state.selectedVersion;
    let selectedVersionType: AccessReportsAdminVersionTypeEnum = this.state.selectedVersionType;

    if (!!accessReportsWizard) {
      // maybe to-do: add some logic in here that creates a new version if there are none at all?
      // temporary, to account for null versions being returned:
      accessReportsWizard.accessReportWizardVersions = accessReportsWizard.accessReportWizardVersions.filter(x => !!x);

      if (this.props.location.pathname.includes("product")) {
        // when refreshing inside the product wizard
        selectedVersion = accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated);
        selectedVersionType = AccessReportsAdminVersionTypeEnum.InProgressVersion;
      } else if (!!selectedVersion) {
        // if a selected version exists, update it
        const updatedSelectedVersion = accessReportsWizard.accessReportWizardVersions.find(x => x.accessReportWizardVersionId === selectedVersion.accessReportWizardVersionId);

        if (!!updatedSelectedVersion && !updatedSelectedVersion.dateActivated) {
          selectedVersion = updatedSelectedVersion;
        } else {
          // deselect the selected version and version type if this update was triggered by an activation
          selectedVersion = null;
          selectedVersionType = null;
        }
      }

      const checkedOutVersion: AccessReportsWizardVersionModel = accessReportsWizard.accessReportWizardVersions.find(x => !!x.checkedOutDate);

      // determine who, if anyone, has checked out a version
      if (!!checkedOutVersion && checkedOutVersion.checkedOutUserId !== null) {
        if (checkedOutVersion.checkedOutUserId === this.props.user?.userId) {
          checkedOutUsername = this.props.user?.displayName ? this.props.user.displayName : `User ${this.props.user.userId.toString()}`;
          isCheckedOutByCurrentUser = true;
        } else {
          // if the currently checked-out template is checked out by a user different than the current one,
          // retrieve the user who checked-out the template's name in order to let the current user know
          if (!isNil(checkedOutVersion.checkedOutUserId)) {
            const checkedOutUser: UserModelFromApi = await this.userService.getUser(checkedOutVersion.checkedOutUserId);

            if (!!checkedOutUser) {
              checkedOutUsername = checkedOutUser.formalName ? checkedOutUser.formalName : `User ${checkedOutUser.userId.toString()}`;
            }
          }
        }
      }
    }

    const disableUserActions: boolean = this.disableUserActions(isCheckedOutByCurrentUser, this.state.selectedVersionType);

    this.setState({
      accessReportsWizard,
      checkedOutUsername,
      disableUserActions,
      isCheckedOutByCurrentUser,
      loading: false,
      selectedVersion,
      selectedVersionType,
    });
  }
}

export { AccessReportsAdmin };
